import { Box, CircularProgress } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import { TABLE_ROWS_PER_PAGE_OPTIONS, TABLE_WIDTH_PARTNER } from 'lib/tableLib'
import { orderBy } from 'lodash'
import T from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import ConfirmBar from 'components/atoms/ConfirmBar'
import DownloadBar from 'components/atoms/DownloadBar'
import FilterRange from 'components/atoms/FilterRange'
import FilterSelect from 'components/atoms/FilterSelect'
import LogIcon from 'components/atoms/LogIcon'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'

import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useInputOutput from 'hooks/useInputOutput'
import useLogs from 'hooks/useLogs'
import useSort from 'hooks/useSort'
import useTablePaginationSync from 'hooks/useTablePaginationSync'

import AssignmentConfirmationDialog from '../Assignment/AssignmentConfirmationDialog'

import useSummaryFilters from './useSummaryFilters'

const TABLE_WIDTH_COMMON = 80
const TABLE_WIDTH_NUMBER = 90

const createColumns = (
  t,
  session,
  cars,
  partners,
  allOption,
  summaryFilters
) => {
  const {
    filters,
    filtersData,
    setAcceptedRange,
    setAcceptedWithRepriceRange,
    setNotChosenRange,
    setPartners,
  } = summaryFilters

  return [
    {
      accessor: 'name',
      Header: (
        <TableHeaderCell
          label={t('assignment.summary.table.partner')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.partners}
              value={filters.partners}
              handleChange={setPartners}
              multiple
              hasSearchField
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_PARTNER,
        },
      },
    },
    {
      accessor: 'accepted',
      Header: (
        <TableHeaderCell
          label={t('assignment.summary.table.accepted')}
          filter={
            <FilterRange
              minBound={filtersData.acceptedMin}
              maxBound={filtersData.acceptedMax}
              min={filters.acceptedMin}
              max={filters.acceptedMax}
              handleRangeChange={setAcceptedRange}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_COMMON,
        },
      },
    },
    {
      accessor: 'acceptedWithReprice',
      Header: (
        <TableHeaderCell
          label={t('assignment.summary.table.acceptedWithReprice')}
          filter={
            <FilterRange
              minBound={filtersData.acceptedWithRepriceMin}
              maxBound={filtersData.acceptedWithRepriceMax}
              min={filters.acceptedWithRepriceMin}
              max={filters.acceptedWithRepriceMax}
              handleRangeChange={setAcceptedWithRepriceRange}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_NUMBER,
        },
      },
    },
    {
      accessor: 'notChosen',
      Header: (
        <TableHeaderCell
          label={t('assignment.summary.table.notChosen')}
          filter={
            <FilterRange
              minBound={filtersData.notChosenMin}
              maxBound={filtersData.notChosenMax}
              min={filters.notChosenMin}
              max={filters.notChosenMax}
              handleRangeChange={setNotChosenRange}
              step={0.5}
            />
          }
        />
      ),
    },
  ]
}
export default function AssignmentSummary({
  session,
  partners,
  cars,
  handleValidation,
  handleConfirm,
  readOnly,
  contentIsReadOnly,
}) {
  const { t } = useTranslation()

  const [filteredItems, setFilteredItems] = useState([])

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()

  const { saveXls } = useInputOutput()

  const { order, setSort } = useSort('partnerGroup')

  const summaryFilters = useSummaryFilters()

  const { filters, initFilters, filter } = summaryFilters

  const handleFilters = useDebouncedCallback(
    (items, filters, setFilteredItems, filter) =>
      setFilteredItems(filter(items, filters)),
    100
  )

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleConfirm)

  const allOption = useMemo(
    () => getOption(t('common.form.all'), OPTION_ALL_VALUE),
    [t]
  )

  const columns = useMemo(
    () => createColumns(t, session, cars, partners, allOption, summaryFilters),
    [t, session, cars, partners, allOption, summaryFilters]
  )

  const preparedItems = useMemo(
    () =>
      partners.map((partner) => {
        const assignedCars = cars.filter(
          (car) => car.assignedPartnerId === partner.partnerId
        )
        return {
          ...partner,
          name: partner.name,
          accepted: assignedCars.filter((car) =>
            car.acceptedChoiceList.includes(partner.partnerId)
          ).length,
          acceptedWithReprice: assignedCars.filter((car) =>
            car.acceptedWithRepriceChoiceList.includes(partner.partnerId)
          ).length,
          notChosen: assignedCars.filter(
            (car) =>
              !car.acceptedChoiceList.includes(partner.partnerId) &&
              !car.acceptedWithRepriceChoiceList.includes(partner.partnerId)
          ).length,
        }
      }),
    [partners, cars]
  )

  useEffect(() => {
    if (!preparedItems?.length || filters?.initialized) {
      return
    }

    initFilters(preparedItems)
  }, [initFilters, filters?.initialized, preparedItems.length, preparedItems])

  useEffect(() => {
    handleFilters(preparedItems, filters, setFilteredItems, filter)
  }, [filter, filters, handleFilters, partners, preparedItems])

  const sortedPartners = useMemo(
    () => orderBy(filteredItems, [order?.column], [order?.direction]),
    [filteredItems, order?.column, order?.direction]
  )

  const {
    page,
    rowsPerPage,
    pagedItems,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useTablePaginationSync({ items: sortedPartners })

  const handleDownload = async () => {
    const titlesRow = [
      t('common.excel.partner'),
      t('assignment.excel.assignedAccepted'),
      t('assignment.excel.assignedAcceptedWithReprice'),
      t('assignment.excel.assignedNotChoosen'),
    ]

    const rows = [
      titlesRow,
      ...preparedItems.map((partner) => [
        partner.name,
        partner.accepted,
        partner.acceptedWithReprice,
        partner.notChosen,
      ]),
    ]

    saveXls('summary', rows)
  }

  return (
    <>
      <Box mt={1} data-testid="assignment-summary" />
      <ConfirmBar
        label={t('assignment.car-assignment.confirmButton')}
        right={
          <>
            <Collapse orientation="horizontal" in={isFetchingLogs}>
              <div style={{ marginLeft: 4, marginRight: 4 }}>
                <CircularProgress size={20} />
              </div>
            </Collapse>
            <LogIcon
              disabled={isFetchingLogs}
              handleClick={() =>
                saveLogs(`logs_assignment_${session?.sessionId}`, {
                  area: t('common.logs.assignment'),
                  sessionId: session?.sessionId,
                })
              }
            />
            <DownloadBar handleDownload={handleDownload} />
          </>
        }
        disabled={
          !handleValidation() ||
          !!session?.assignmentEndedAt ||
          readOnly ||
          contentIsReadOnly
        }
        handleConfirm={handleOpenConfirmationDialog}
      />
      <Box pb={2} />
      <Table
        items={pagedItems}
        columns={columns}
        total={filteredItems.length}
        hasPagination={true}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        sortable
        orderColumn={order?.column}
        orderDirection={order?.direction}
        handleSort={setSort}
      />
      <AssignmentConfirmationDialog
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
      />
    </>
  )
}

AssignmentSummary.propTypes = {
  session: T.object.isRequired,
  partners: T.array,
  cars: T.array,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
